import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import useOptimisticUpdate from './useOptimisticUpdate';

export default function useSprintBoard({ showToast, project, users }) {
  const [sprint, setSprint] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isKanban = project?.type === 'kanban';
  const isRetro = project?.type === 'retrospective';

  const projectUsers = useMemo(() => {
    if (!project?.members) return users;
    return users.filter((u) => project.members.includes(u.uid));
  }, [users, project]);

  const loadItems = useCallback(async (sprintId) => {
    const data = await api.getSprintItems(sprintId);
    setItems(data);
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        let active = await api.getActiveSprint(project.id);
        if (!active) {
          if (isKanban) {
            active = await api.createKanbanBoard(project.id);
          } else if (isRetro) {
            const now = new Date();
            const end = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
            active = await api.createSprint({ name: 'Retro 1', startDate: now, endDate: end, projectId: project.id });
          } else {
            const now = new Date();
            const end = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
            active = await api.createSprint({ name: 'Sprint 1', startDate: now, endDate: end, projectId: project.id });
          }
        } else {
          // Migrate legacy kanban "Board" sprints with far-future endDate
          const endDate = active.endDate?.toDate ? active.endDate.toDate() : new Date(active.endDate);
          if (isKanban && endDate.getFullYear() >= 2099) {
            const startDate = active.startDate?.toDate ? active.startDate.toDate() : new Date(active.startDate);
            const migratedEnd = new Date(startDate.getTime() + 14 * 24 * 60 * 60 * 1000);
            const updates = { endDate: migratedEnd };
            if (active.name === 'Board') updates.name = 'Sprint 1';
            active = await api.updateSprint(active.id, updates);
          } else if (endDate < new Date()) {
            active = await api.closeSprint(active.id);
          }
        }
        setSprint(active);
        await loadItems(active.id);
      } catch (err) {
        showToast(err.message || 'Failed to load sprint', 'error');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [loadItems, showToast, project.id, isKanban, isRetro]);

  const applyUpdate = useOptimisticUpdate(items, setItems, showToast);

  const CONTAINER_CASCADE_TYPES = ['feature', 'requirement', 'epic'];

  const handleStatusChange = async (itemId, newStatus) => {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;

    const ok = await applyUpdate(itemId, { status: newStatus }, () => api.updateSprintItem(itemId, { status: newStatus }), 'Failed to update status');
    if (!ok) return;

    // If a feature/epic/requirement is moved to done, cascade to all children
    if (newStatus === 'done' && CONTAINER_CASCADE_TYPES.includes(item.type)) {
      const childIds = await api.batchUpdateChildrenStatus(itemId, 'done', sprint.id);
      if (childIds.length > 0) {
        setItems((prev) => prev.map((i) => childIds.includes(i.id) ? { ...i, status: 'done' } : i));
      }
    }

    // After any status change, check if parent should auto-complete or revert
    if (item.parentId) {
      await checkParentCompletion(item.parentId, itemId, newStatus);
    }
  };

  const checkParentCompletion = async (parentId, changedChildId, childNewStatus) => {
    const parent = items.find((i) => i.id === parentId);
    if (!parent || !CONTAINER_CASCADE_TYPES.includes(parent.type)) return;

    const siblings = items.filter((i) => i.parentId === parentId && i.id !== changedChildId);
    const allSiblingsDone = siblings.every((s) => s.status === 'done');

    if (childNewStatus === 'done' && allSiblingsDone) {
      // All children done → auto-complete parent
      const updated = await api.updateSprintItem(parentId, { status: 'done' });
      setItems((prev) => prev.map((i) => (i.id === parentId ? updated : i)));
      // Recurse up
      if (parent.parentId) {
        await checkParentCompletion(parent.parentId, parentId, 'done');
      }
    } else if (childNewStatus !== 'done' && parent.status === 'done') {
      // A child was un-done while parent was done → revert parent to in_progress
      const updated = await api.updateSprintItem(parentId, { status: 'in_progress' });
      setItems((prev) => prev.map((i) => (i.id === parentId ? updated : i)));
      if (parent.parentId) {
        await checkParentCompletion(parent.parentId, parentId, 'in_progress');
      }
    }
  };

  const handleChildStatusToggle = async (childId, isDone) => {
    const newStatus = isDone ? 'done' : 'in_progress';
    await handleStatusChange(childId, newStatus);
  };

  const handleAssigneeChange = async (itemId, assigneeId) => {
    const value = assigneeId || null;
    await applyUpdate(itemId, { assigneeId: value }, () => api.updateSprintItem(itemId, { assigneeId: value }), 'Failed to update assignee');
  };

  const handleAddItem = async (itemData) => {
    const maxOrder = items.reduce((max, i) => Math.max(max, i.order || 0), 0);
    const created = await api.createSprintItem({
      ...itemData,
      sprintId: sprint.id,
      order: maxOrder + 1,
      location: itemData.location || 'board',
    });
    setItems((prev) => [...prev, created]);
    return created;
  };

  const handleUpdateItem = async (itemId, data) => {
    const item = items.find((i) => i.id === itemId);
    const updated = await api.updateSprintItem(itemId, data);
    setItems((prev) => prev.map((i) => (i.id === itemId ? updated : i)));
    if (data.status && data.status !== item?.status && item?.parentId) {
      await checkParentCompletion(item.parentId, itemId, data.status);
    }
    return updated;
  };

  const handleDeleteItem = async (itemId) => {
    const item = items.find((i) => i.id === itemId);
    if (item && CONTAINER_CASCADE_TYPES.includes(item.type)) {
      const deletedIds = await api.deleteItemWithChildren(itemId, items);
      setItems((prev) => prev.filter((i) => !deletedIds.includes(i.id)));
    } else {
      await api.deleteSprintItem(itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
    }
  };

  const handleUpdateSprint = async (data) => {
    const updated = await api.updateSprint(sprint.id, data);
    setSprint(updated);
    return updated;
  };

  const handleEndSprint = async () => {
    const newSprint = await api.closeSprint(sprint.id, isRetro ? { skipItemMove: true } : {});
    setSprint(newSprint);
    await loadItems(newSprint.id);
    return newSprint;
  };

  // Board items: include items with location=board, but exclude children of board features
  // (they display inside the expandable feature card, not as standalone cards)
  const boardExpandableIds = new Set(
    items
      .filter((i) => (i.type === 'feature' || i.type === 'epic') && i.location === 'board')
      .map((i) => i.id)
  );
  const boardItemChildIds = new Set();
  if (boardExpandableIds.size > 0) {
    const collectChildren = (parentId) => {
      items.filter((i) => i.parentId === parentId).forEach((child) => {
        boardItemChildIds.add(child.id);
        collectChildren(child.id);
      });
    };
    boardExpandableIds.forEach((eid) => collectChildren(eid));
  }

  const boardItems = items.filter((i) => {
    if (boardItemChildIds.has(i.id)) return false;
    return !i.location || i.location === 'board';
  });

  const reloadItems = useCallback(async () => {
    if (sprint) await loadItems(sprint.id);
  }, [sprint, loadItems]);

  return {
    sprint,
    items,
    boardItems,
    loading,
    isKanban,
    isRetro,
    projectUsers,
    handleStatusChange,
    handleAssigneeChange,
    handleChildStatusToggle,
    handleAddItem,
    handleUpdateItem,
    handleDeleteItem,
    handleUpdateSprint,
    handleEndSprint,
    reloadItems,
  };
}
